import { logSwallowed } from '@/lib/errors';
// ============================================================================
// useObjectFormModel — ИМПЕРАТИВНАЯ МОДЕЛЬ ФОРМЫ ОБЪЕКТА (вынесено из
// AutoObjectForm, РЕФАКТОРИНГ 1:1 БЕЗ изменения поведения): ВСЕ состояния/
// эффекты/вычисляемые значения/обработчики между началом функции и JSX.
// AutoObjectForm — тонкий компонент (типы + вызов хука + JSX). Порядок хуков,
// зависимостей и веток сохранён дословно (type объявлен ДО хуков — TDZ).
// ============================================================================
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { viewForDevice, parseViewSettings } from '@/lib/viewSettings';
import { useFormStore, buildFormKey } from '@/lib/formStore';
import { endFormSession } from '@/lib/formApi';
import { collectSaveData, collectFormAttributes, collectExtraFields, collectFormValues } from '@/lib/formCollect';
import { validateFormFields } from '@/lib/formValidate';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { AutoFormField } from '@/components/autoforms/AutoFormField';
import type { EnumOption } from '@/components/forms/FormFieldRenderer';
import { loadEnumOptions } from '@/lib/enumOptions';
import { dataService } from '@/lib/dataService';
import { normalizePickTypes, buildSelectionFilters, buildCreateData, parseModulePickerType } from '@/components/autoforms/objectFormHelpers';
import { formBindingService } from '@/lib/formBindingService';
import { getSDK } from '@/store/sdkStore';
import { ensureFileBlob } from '@/lib/fileSync';
import { createLogger } from '@/lib/logger';
import { promptDialog, confirmDialog } from '@/lib/dialogs';
import { emitDataChanged } from '@/lib/dataEvents';
import { useObjectLock } from '@/hooks/useObjectLock';
import { useAuditHistory } from '@/hooks/useAuditHistory';
import { useAuthStore } from '@/store/authStore';
import { FormModule, createFormAPI } from '@/components/autoforms/FormModule';
import { useToastStore } from '@/store/toastStore';
import { friendlyError } from '@/lib/errors';
import { translate } from '@/lib/i18n';
import type { CondFormat, CondContext } from '@/lib/conditionalFormat';
import { loadFieldExtensions } from '@/lib/fieldExtensions';
import { settingsStore } from '@/lib/settingsStore';
import { readThrough } from '@/lib/offlineCache';
import { useContextChatStore } from '@/lib/contextChat';
import type { PickerAttrRef, AnyRefPickerState, MultiPickState, ModulePickerState } from '@/components/autoforms/ObjectFormDialogs';

// Стабильный пустой объект значений формы (НЕ пересоздавать в selector —
// иначе бесконечный ререндер, React #185).
const EMPTY_FORM_VALUES: Record<string, unknown> = {};

// Модульный реестр форм, для которых onOpen (модуль формы) уже выполнен —
// устойчив к ПЕРЕМОНТИРОВАНИЮ React (ref сбрасывается, модульный Set — нет).
// onOpen заполняет реквизиты ФОРМЫ (form.setField) из данных и НЕ должен
// перезапускаться повторно (иначе затрёт пользовательский ввод). Удаляется при
// размонтировании (cleanup) — новая жизнь формы снова выполнит onOpen.
const openedFormKeys = new Set<string>();

const log = createLogger('AutoObjectForm');

// Резолв типа объекта ИСТОЧНИКА динамического списка (детерминированно):
// задача → справочник → документ → регистр. Возвращает null, если таблица не
// найдена среди известных типов (вызывающий показывает явную ошибку).
async function resolveDlSourceType(srcTable: string): Promise<string | null> {
  const tt: any = await getSDK().task.listTaskTypes({});
  if ((tt?.items || []).some((t: any) => t.code === srcTable)) return 'task';
  const cat: any = await getSDK().metadata.listCatalogs({});
  if ((cat?.items || []).some((c: any) => c.code === srcTable)) return 'catalog';
  const doc: any = await getSDK().metadata.listDocuments({});
  if ((doc?.items || []).some((d: any) => d.code === srcTable)) return 'document';
  const reg: any = await getSDK().metadata.listRegisters({});
  if ((reg?.items || []).some((r: any) => r.code === srcTable)) return 'register';
  return null;
}

export interface FieldDef {
  id?: string;
  code?: string;
  name?: string;
  synonym?: string;
  dataType?: string;
  data_type?: string;
  isRequired?: boolean;
  is_required?: boolean;
  order?: number;
  refTable?: string;
  ref_table?: string;
  refCatalogCode?: string;
  /** Скрыто по умолчанию (как в 1С): не выводится в авто-формах/списках/карточках,
      показывается только при ЯВНОМ выборе (макет/колонки кастомной формы) */
  hiddenByDefault?: boolean;
  hidden_by_default?: boolean;
  /** Значение по умолчанию (metadata_fields.default_value): подставляется при
      создании НОВОЙ записи, если пользователь значение не задал. */
  defaultValue?: string;
  default_value?: string;
}

export interface TabularSectionDef {
  id?: string;
  code?: string;
  name?: string;
  synonym?: string;
  columns?: Array<{ name: string; dataType?: string; refTable?: string }>;
}

export interface UseObjectFormModelInput {
  objectType: string;
  objectName: string;
  objectId?: string;
  initialData?: Record<string, any>;
  onClose: () => void;
  /** Вызывается после создания НОВОЙ записи (для форм выбора) */
  onCreated?: (id: string, name: string) => void;
  /** inline=true — форма открывается КАК ЭКРАН внутри рабочей области
   *  (state-режим: не поверх, без оверлея; панели приложения на месте).
   *  default false = модальное окно (для пикеров/диалогов). */
  inline?: boolean;
}

export function useObjectFormModel({ objectType, objectName, objectId, initialData, onClose, onCreated }: UseObjectFormModelInput) {
  const [fields, setFields] = useState<FieldDef[]>([]);
  // Реестр дополнений платформы: загрузка до рендера полей (вид «Дополнение
  // адреса» по default_for; SSoT — сервер) + форс-пере-рендер после загрузки
  const [extReady, setExtReady] = useState(false);
  useEffect(() => {
    void loadFieldExtensions().then(() => setExtReady(true)).catch(() => setExtReady(true));
  }, []);
  const [tabularSections, setTabularSections] = useState<TabularSectionDef[]>([]);
  const [data, setData] = useState<Record<string, any>>(initialData || {});
  // «Создать» из дин.списка: форма объекта источника + prefill из отборов
  const [dlCreate, setDlCreate] = useState<{ type: string; name: string; initial: Record<string, unknown> } | null>(null);
  // ВЫБОР строки дин.списка: открыть запись источника (существующую) вложенной формой
  const [dlOpen, setDlOpen] = useState<{ type: string; name: string; id: string } | null>(null);
  // ВНИМАНИЕ: type объявлен ДО хуков — используется в зависимостях useEffect
  // (иначе TDZ: «Cannot access 'type' before initialization»)
  const type = objectType.toLowerCase();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Ошибка СОХРАНЕНИЯ — баннер поверх формы (в отличие от error — ошибки загрузки)
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Меню «Ещё» в экшен-панели (отмена проведения — всегда в меню)
  const [moreOpen, setMoreOpen] = useState(false);
  // id записи после сохранения (для проведения: save → postDocument)
  const recordIdRef = useRef<string | null>(null);
  // Геометрия окна формы (хранилище настроек): ключ form.{тип}.{объект}
  const geometryKey = `form.${type}.${objectName}`;
  const formGeometry = settingsStore.getJSON<{ maximized?: boolean; pos?: { x: number; y: number } }>(geometryKey, {});
  const [customForm, setCustomForm] = useState<any>(null);
  // АТОМАРНЫЙ СТОР ЗНАЧЕНИЙ ФОРМЫ (luna5): значения в formStore по formKey —
  // единый источник. customValues — подписка на стор (ререндер при изменении);
  // setCustomValues пишет в стор (функционально). Убирает хрупкую проп-цепочку
  // рендера (поле читает значение из стора напрямую) и гонки «выбрал — не встало».
  // Ключ — ЕДИНЫЙ конструктор (formStore.buildFormKey): тип.объект.recordId |
  // тип.объект.new — значения привязаны к ЗАПИСИ (значения одной не видны другой).
  const formKey = buildFormKey(type, objectName, objectId);
  // ВАЖНО: НЕ возвращать новый {} при отсутствии formKey в сторе — zustand
  // сравнивает selector по Object.is → новая ссылка каждый рендер = бесконечный
  // цикл (React #185). Используем СТАБИЛЬНЫЙ пустой объект-константу.
  const NO_VALUES: Record<string, unknown> = EMPTY_FORM_VALUES;
  const customValues = useFormStore((s) => s.values[formKey] ?? NO_VALUES);
  const setCustomValues = useCallback(
    (updater: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)) => {
      useFormStore.getState().setForm(formKey, updater);
    },
    [formKey],
  );
  // ЕДИНСТВЕННАЯ точка ЗАКРЫТИЯ формы: завершает СЕАНС значений (formStore.
  // endSession) и вызывает внешний onClose. Так «новая запись» не наследует
  // значения предыдущей (черновик .new очищается), а существующая — сохраняет
  // несохранённый ввод. Все внутренние ветки закрытия (Сохранить-закрыть,
  // команды, модуль формы, крестик окна) обязаны идти через closeForm.
  const closeForm = useCallback(() => {
    endFormSession(formKey);
    onClose();
  }, [formKey, onClose]);
  // ВАЖНО: НЕ чистим стор и НЕ сбрасываем onOpen-флаг при размонтировании.
  // Перемонтирование AutoObjectForm происходит и при blocking-модалках (пикер
  // «...»), и при ре-рендерах родителя — сброс initialized/значений приводил к
  // ПОВТОРНОМУ onOpen (goja-модуль формы form.setField('Адрес', presentation)),
  // который затирал пользовательский выбор (баг «на новой работает, на
  // существующей нет»). Значения формы живут в сторе по formKey (включает
  // recordId) — новый formKey = новые данные; очистка — ТОЛЬКО в closeForm
  // (реальное закрытие формы), см. endFormSession.
  useEffect(() => {
    return () => {
      // НЕ reset(formKey) и НЕ openedFormKeys.delete(formKey) — см. комментарий выше
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);
  const [customDisplayNames, setCustomDisplayNames] = useState<Record<string, string>>({});
  const [customTsData, setCustomTsData] = useState<Record<string, Array<Record<string, unknown>>>>({});
  const [formAttrs, setFormAttrs] = useState<Array<{ name: string; source?: { type?: string; table?: string; filters?: Array<{ field: string; condition: string; value?: string }>; commands?: string[] } }>>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Ошибка валидации обязательных полей — отдельный баннер ПОВЕРХ формы (не заменяет её)
  const [validationError, setValidationError] = useState<string | null>(null);
  // «Вид объекта» (SSoT — view_settings, как в 1С): object.view=card →
  // форма отображается КАРТОЧКОЙ (поля по важности), кнопка «Редактировать» —
  // обычная форма. Для новых записей — всегда обычная форма.
  const { isMobile } = useBreakpoint();
  const [cardView, setCardView] = useState(false);
  useEffect(() => {
    if (!objectId || (type !== 'catalog' && type !== 'document')) return;
    let cancelled = false;
    (async () => {
      try {
        const sdk = getSDK();
        const meta: any = type === 'document'
          ? await sdk.metadata.getDocument({ id: objectName })
          : await sdk.metadata.getCatalog({ id: objectName });
        if (cancelled) return;
        const vs = parseViewSettings(String(meta?.viewSettings || ''));
        setCardView(viewForDevice(vs, 'object', isMobile)?.view === 'card');
      } catch (e) { logSwallowed(e, 'components/autoforms/useObjectFormModel.tsx:205'); }
    })();
    return () => { cancelled = true; };
  }, [type, objectName, objectId, isMobile]);

  // Для НОВОЙ записи: временный id родителя для строк ТЧ (parent_id);
  // после сохранения записи строки ремапятся на серверный id.
  const [tempParentId] = useState(() => (objectId ? '' : crypto.randomUUID ? crypto.randomUUID() : String(Date.now())));

  const tsParentId = objectId || tempParentId;

  const schema = 'public';
  const table = type === 'entity' || type === 'entities' ? 'entities' : objectName.toLowerCase();
  // Регистр: физическая таблица данных — movements_table (накопления) или код (сведения);
  // определяется при загрузке метаданных (getRegister), до этого — код регистра.
  const [registerTable, setRegisterTable] = useState('');
  const dataTable = registerTable || table;

  const lock = useObjectLock(schema, table, objectId, type === 'catalog' || type === 'document' || type === 'entity');
  const auditEntityType = type === 'catalogs' ? 'catalog' : type === 'documents' ? 'document' : type === 'registers' ? 'register' : type;
  const [auditEnabled, setAuditEnabled] = useState(false);
  const audit = useAuditHistory(auditEntityType, objectName, auditEnabled && (type === 'catalog' || type === 'document' || type === 'register' || type === 'entity'));
  const [historyOpen, setHistoryOpen] = useState(false);
  // Иерархия справочника (как в 1С): '' | 'groups_and_items' | 'items'
  const [catalogHierarchyType, setCatalogHierarchyType] = useState('');
  // Владельцы справочника: коды справочников-владельцев
  const [catalogOwners, setCatalogOwners] = useState<Array<{ code: string; type: string }>>([]);
  // Подчинённые справочники (как в 1С): у этого объекта-владельца; вкладки в форме
  const [subordinates, setSubordinates] = useState<Array<{ name: string; synonym: string }>>([]);
  // ЕДИНАЯ активная вкладка (SRP — вместо N булевых флагов):
  //   'main'              — основное содержимое;
  //   'sub:<name>'        — подчинённый справочник;
  //   'tasks' / 'files'   — задачи/файлы (по составу определяемых типов связей).
  const [activeTab, setActiveTab] = useState('main');
  // Вкладка «Задачи» — ТОЛЬКО если объект включён в определяемый тип
  // «ЗадачиПоДокументам» (измерение регистра task_links; SSoT — сервер):
  // состав определяемого типа = список документов/справочников, у которых
  // рендерится форма списка задач (как в 1С).
  const [tasksLinked, setTasksLinked] = useState(false);
  // Вкладка «Файлы» — ТОЛЬКО если объект включён в определяемый тип
  // «ФайлыПоДокументам» (измерение регистра file_links; SSoT — сервер):
  // связь «файл ↔ объект» — РЕГИСТР, а не владелец справочника «Файлы».
  const [filesLinked, setFilesLinked] = useState(false);
  useEffect(() => {
    // Вкладки «Задачи»/«Файлы» — по составу определяемых типов связей (SSoT —
    // сервер, listDefinedTypes): объект включён → вкладка. Дескриптор по типу
    // (как серверный descriptorToTable): справочник/документ/заявка/задача.
    const descMap: Record<string, string> = { catalog: 'Справочник.', document: 'Документ.', order: 'Заявка.', task: 'Задача.' };
    const prefix = descMap[type];
    if (!prefix) return;
    let cancelled = false;
    getSDK().metadata.listDefinedTypes({})
      .then((r: any) => {
        if (cancelled) return;
        const items = (r?.items || []) as any[];
        const descriptor = prefix + objectName;
        const inDT = (name: string) => {
          const dt = items.find((d: any) => d.name === name);
          return !!dt && ((dt.types || []) as string[]).includes(descriptor);
        };
        setTasksLinked(inDT('ЗадачиПоДокументам'));
        setFilesLinked(inDT('ФайлыПоДокументам'));
      })
      .catch((e: any) => {
        log.error('listDefinedTypes failed', { error: e?.message || String(e) });
        setTasksLinked(false);
        setFilesLinked(false);
      });
    return () => { cancelled = true; };
  }, [type, objectName]);
  // Режим выбора ссылочных реквизитов (из свойств формы): '' | 'items' | 'groups' | 'both'
  const [attrSelectModes, setAttrSelectModes] = useState<Record<string, string>>({});
  // Значения перечислений: код перечисления → [{value, label}]
  const [enumOptions, setEnumOptions] = useState<Record<string, EnumOption[]>>({});
  // Условное оформление (как в 1С, на стилях): правила + стили + контекст
  const [condRules, setCondRules] = useState<CondFormat[]>([]);
  const [condStyles, setCondStyles] = useState<Record<string, any>>({});
  const condCtx: CondContext = useMemo(() => ({
    currentUserId: useAuthStore.getState().user?.id || '',
    currentUserRoles: useAuthStore.getState().user?.roles || [],
    isAdmin: useAuthStore.getState().isAdmin(),
  }), []);
  const [formTitle, setFormTitle] = useState(translate('form.title', { name: objectName, action: objectId ? translate('common.edit') : translate('common.creating') }));
  const [hiddenFields, setHiddenFields] = useState<Record<string, boolean>>({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickingAttr, setPickingAttr] = useState<PickerAttrRef | null>(null);
  // «Поле множественного значения» (ТЧ вид='Поле'): выбор → новая строка ТЧ
  const [multiPick, setMultiPick] = useState<MultiPickState | null>(null);
  const [modulePicker, setModulePicker] = useState<ModulePickerState | null>(null);
  // «Любая ссылка» (anyref): пикер «выбор типа → выбор значения»
  const [anyRefPicker, setAnyRefPicker] = useState<AnyRefPickerState | null>(null);

  // Типы для «Любой ссылки»: все ссылочные — справочники, документы, задачи, пользователи
  const openAnyRefPicker = async (field: string, label: string) => {
    const types: Array<{ objectType: string; objectName: string; name: string }> = [];
    try {
      const [cats, docs] = await Promise.all([
        getSDK().metadata.listCatalogs({}),
        getSDK().metadata.listDocuments({}),
      ]);
      for (const c of (cats as any)?.items || []) types.push({ objectType: 'catalog', objectName: c.code, name: c.synonym || c.name || c.code });
      for (const d of (docs as any)?.items || []) types.push({ objectType: 'document', objectName: d.code, name: d.synonym || d.name || d.code });
    } catch (e) { logSwallowed(e, 'components/autoforms/useObjectFormModel.tsx:307'); }
    types.push({ objectType: 'task', objectName: 'tasks', name: translate('form.type.task') });
    types.push({ objectType: 'entity', objectName: 'entities', name: translate('form.type.user') });
    // Файл: загрузка в системное хранилище (SSoT — FileService), значение = id файла
    types.push({ objectType: 'file', objectName: 'files', name: translate('form.type.file') });
    setAnyRefPicker({ field, label, types, pickingType: null });
  };
  const isAdmin = useAuthStore((s) => s.isAdmin());
  const currentUser = useAuthStore((s) => s.user);
  // Контекстное обсуждение объекта: панель чата + бейдж непрочитанных
  // (SSoT — contextChat: один чат на объект, счётчики по контекстам)
  const [chatOpen, setChatOpen] = useState(false);
  const chatUnread = useContextChatStore((s) => s.unreadFor(type, objectId || ''));
  // История изменений: админ или роль/разрешение «просмотр истории»
  const canViewAudit = isAdmin
    || (Array.isArray(currentUser?.permissions) && currentUser.permissions.includes('audit:view'))
    || (Array.isArray(currentUser?.roles) && currentUser.roles.includes('Просмотр истории'));
  // Права текущего пользователя на объект (как в 1С): сервер заполняет rights по ролям JWT
  const [objectRights, setObjectRights] = useState<Record<string, any> | null>(null);

  // Подчинённые справочники (как в 1С): справочники, у которых этот объект —
  // владелец. Вкладки появляются ТОЛЬКО у сохранённых записей (objectId):
  // у новых нет ссылки на пустоту — ни отбора, ни создания.
  useEffect(() => {
    if (!objectId || !objectName || (type !== 'catalog' && type !== 'document')) {
      setSubordinates([]);
      setActiveTab('main');
      return;
    }
    let cancelled = false;
    getSDK().metadata.listCatalogs({}).then((r: any) => {
      if (cancelled) return;
      const subs = ((r?.items || []) as any[])
        .filter((c: any) => ((c.resolvedOwners || c.owners) as any[] || []).some((o: any) => String(o.code ?? o) === objectName && String(o.type || 'catalog') === type))
        .map((c: any) => ({ name: String(c.code || ''), synonym: String(c.synonym || c.name || c.code || '') }))
        .filter((s) => !!s.name);
      setSubordinates(subs);
    }).catch((e) => logSwallowed(e, 'components/autoforms/useObjectFormModel.tsx:344'));
    return () => { cancelled = true; };
  }, [objectId, objectName, type]);

  // ==========================================================================
  // Статусная модель (машина состояний, как в 1С): панель статуса объекта —
  // текущий статус + доступные переходы (роли из JWT, SSoT: сервер).
  // ==========================================================================
  const statusEntityType = type === 'catalog' ? 'catalog' : type === 'document' ? 'document' : type === 'order' ? 'order' : '';
  const [statusSchema, setStatusSchema] = useState<{ fieldName: string; schemaName: string; statuses: { code: string; name: string }[] } | null>(null);
  const [statusPanel, setStatusPanel] = useState<{ currentCode: string; currentName: string; transitions: any[] } | null>(null);
  const [statusBusy, setStatusBusy] = useState(false);
  const statusField = statusSchema?.fieldName || 'status';

  // ==========================================================================
  // Статус ЗАДАЧИ — из СТАТУСНОЙ СХЕМЫ типа задачи (SSoT — сервер): селект
  // «Статус» предлагает ВСЕ статусы схемы (перечисление для задач не нужно),
  // а для НОВОЙ задачи значение по умолчанию — начальный (is_initial) статус
  // схемы. Схема читается в эффекте метаданных (до инициализации значений —
  // дефолт попадает и в автоформу, и в кастомную форму).
  // ==========================================================================
  const [taskStatusSchema, setTaskStatusSchema] = useState<{ fieldName: string; statuses: { code: string; name: string }[]; initialCode: string } | null>(null);
  const taskStatusField = type === 'task' ? (taskStatusSchema?.fieldName || 'status_code') : '';
  const taskStatusOptions = useMemo(
    () => (taskStatusSchema?.statuses || []).map((s) => ({ value: s.code, label: s.name })),
    [taskStatusSchema],
  );
  // Поле формы — «Статус» задачи (значения селекта — из схемы, не перечисления)
  const isTaskStatusField = useCallback(
    (code?: string) => type === 'task' && !!taskStatusSchema && (code || '') === taskStatusField,
    [type, taskStatusSchema, taskStatusField],
  );

  // Схема объекта (одна на справочник/документ)
  useEffect(() => {
    if (!statusEntityType || !objectName) return;
    let cancelled = false;
    getSDK().status.getStatusSchema({ entityType: statusEntityType, entityCode: objectName } as any)
      .then((r: any) => {
        if (cancelled || !r?.id) return;
        // Статусы схемы (код → НАЗВАНИЕ): кнопки переходов показываем
        // по-русски («→ На рассмотрении»), а не кодами (review/...).
        setStatusSchema({
          fieldName: r.fieldName || 'status',
          schemaName: r.synonym || r.name || '',
          statuses: (r?.statuses || []).map((s: any) => ({ code: String(s.code || ''), name: String(s.name || s.code || '') })),
        });
      })
      .catch((e) => logSwallowed(e, 'components/autoforms/useObjectFormModel.tsx:373'));
    return () => { cancelled = true; };
  }, [statusEntityType, objectName]);

  // Переходы из текущего статуса записи
  useEffect(() => {
    if (!statusSchema || !statusEntityType || !objectName) return;
    let cancelled = false;
    const fromCode = String(data[statusField] ?? '');
    getSDK().status.getTransitions({ entityType: statusEntityType, entityCode: objectName, fromCode } as any)
      .then((r: any) => {
        if (cancelled) return;
        // Названия целевых статусов (русские) — из схемы: кнопка «→ Отклонена».
        const nameByCode: Record<string, string> = {};
        for (const s of statusSchema.statuses || []) nameByCode[s.code] = s.name;
        setStatusPanel({
          currentCode: r?.currentStatusCode ?? fromCode,
          currentName: r?.currentStatusName || nameByCode[fromCode] || '',
          transitions: (r?.transitions || []).map((t: any) => ({
            ...t,
            toName: nameByCode[String(t?.toCode || '')] || '',
          })),
        });
      })
      .catch((e) => logSwallowed(e, 'components/autoforms/useObjectFormModel.tsx:397'));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusSchema, statusEntityType, objectName, data[statusField]]);

  const applyStatus = async (toCode: string) => {
    if (!objectId) return;
    setStatusBusy(true);
    try {
      await getSDK().status.applyStatus({ entityType: statusEntityType, entityCode: objectName, rowId: objectId, toCode } as any);
      const updated = await dataService.fetchById(schema, table, objectId);
      if (updated) setData((prev) => ({ ...prev, ...updated }));
      // Списки/рабочие столы обновляются сами (статус заявки/задачи изменился).
      emitDataChanged({ table, action: 'updated' });
      useToastStore.getState().showSuccess(translate('status.changed'));
    } catch (e: any) {
      useToastStore.getState().showError(friendlyError(e));
    } finally {
      setStatusBusy(false);
    }
  };

  // Вкладка «Задачи» по предмету (как в 1С: задачи объекта) — сохранённые записи

  // Представление предзаполненного владельца (создание из вкладки подчинённых,
  // повторное открытие): owner_id задан, а {col}_presentation ещё нет —
  // резолвим через СЕРВЕР (ResolveReference, SSoT) — поле «Владелец» показывает имя.
  useEffect(() => {
    const ownerId = data.owner_id;
    if (!ownerId || catalogOwners.length === 0 || data.owner_id_presentation) return;
    let cancelled = false;
    const types = catalogOwners.map((o) => `${o.type === 'document' ? 'Документ' : 'Справочник'}.${o.code}`);
    getSDK().metadata.resolveReference({ types, id: String(ownerId) })
      .then((r: any) => {
        if (!cancelled && r.presentation) {
          setData((prev) => (prev.owner_id === ownerId ? { ...prev, owner_id_presentation: r.presentation } : prev));
        }
      })
      .catch((e) => logSwallowed(e, 'components/autoforms/useObjectFormModel.tsx:435'));
    return () => { cancelled = true; };
  }, [data.owner_id, data.owner_id_presentation, catalogOwners]);

  // Загрузка метаданных + данных через DataService
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        // 1. Метаданные объекта (fields + tabular_sections)
        const sdk = getSDK();
        let objFields: FieldDef[] = [];
        let objTs: TabularSectionDef[] = [];

        if (type === 'catalog' || type === 'catalogs') {
          const cat: any = (await readThrough(`meta:catalog:${objectName}`, () => sdk.metadata.getCatalog({ id: objectName }))).data;
          objFields = (cat?.fields || []) as FieldDef[];
          objTs = (cat?.tabularSections || []) as TabularSectionDef[];
          // Отслеживание прочтения (objects_reads): открытие формы → прочитано
          if (objectId && cat?.trackReads) {
            sdk.metadata.markObjectRead({ objectType: 'catalog', objectCode: objectName, objectId }).catch((e) => { logSwallowed(e, 'components/autoforms/useObjectFormModel.tsx:458'); return undefined; });
          }
          if (!cancelled) setAuditEnabled(!!cat?.isAuditEnabled);
          if (!cancelled) setObjectRights(cat?.rights || null);
          if (!cancelled) {
            setCatalogHierarchyType(String(cat?.hierarchyType || ''));
            setCatalogOwners((((cat?.resolvedOwners || cat?.owners) as any[] || []).map((o: any) => ({ code: String(o.code ?? o), type: String(o.type || 'catalog') }))).filter((o) => !!o.code));
          }
          // Заголовок формы — синоним (Наименование) справочника, не транслит-код
          const displayName = cat?.synonym || cat?.name || objectName;
          if (!cancelled) setFormTitle(translate('form.title', { name: displayName, action: objectId ? translate('common.edit') : translate('common.creating') }));
        } else if (type === 'document' || type === 'documents') {
          const doc: any = (await readThrough(`meta:document:${objectName}`, () => sdk.metadata.getDocument({ id: objectName }))).data;
          objFields = (doc?.fields || []) as FieldDef[];
          objTs = (doc?.tabularSections || []) as TabularSectionDef[];
          if (objectId && doc?.trackReads) {
            sdk.metadata.markObjectRead({ objectType: 'document', objectCode: objectName, objectId }).catch((e) => { logSwallowed(e, 'components/autoforms/useObjectFormModel.tsx:474'); return undefined; });
          }
          if (!cancelled) setAuditEnabled(!!doc?.isAuditEnabled);
          if (!cancelled) setObjectRights(doc?.rights || null);
          const displayName = doc?.synonym || doc?.name || objectName;
          if (!cancelled) setFormTitle(translate('form.title', { name: displayName, action: objectId ? translate('common.edit') : translate('common.creating') }));
        } else if (type === 'register' || type === 'registers') {
          const reg: any = (await readThrough(`meta:register:${objectName}`, () => sdk.metadata.getRegister({ id: objectName }))).data;
          // Поля регистра (измерения/ресурсы/реквизиты) — ЕДИНЫЙ механизм
          // metadata_fields (как в 1С: форма записи = измерения + ресурсы)
          const fieldsResp: any = (await readThrough(`fields:register:${objectName}`, () =>
            sdk.metadata.listFields({ entityType: 'register', entityCode: objectName }),
          )).data;
          objFields = (fieldsResp?.items || []) as FieldDef[];
          // Физическая таблица данных: movements_table (накопления) или код (сведения)
          setRegisterTable(String((reg as any)?.movementsTable || ''));
          if (!cancelled) setAuditEnabled(!!reg?.isAuditEnabled);
          if (!cancelled) setObjectRights(reg?.rights || null);
          const displayName = reg?.synonym || reg?.name || objectName;
          if (!cancelled) setFormTitle(translate('form.title', { name: displayName, action: objectId ? translate('common.edit') : translate('common.creating') }));
        } else if (type === 'order' || type === 'orders') {
          // ЗАЯВКА (встроенный тип, как Задачи): реквизиты — ЕДИНЫЙ механизм
          // metadata_fields (parent_type='order'); данные — таблица {код типа}
          const fieldsResp: any = (await readThrough(`fields:order:${objectName}`, () =>
            sdk.metadata.listFields({ entityType: 'order', entityCode: objectName }),
          )).data;
          objFields = (fieldsResp?.items || []) as FieldDef[];
          const otResp: any = (await readThrough('order-types', () => sdk.order.listOrderTypes({}))).data;
          const ot = (otResp.items || []).find((x: any) => x.code === objectName);
          const displayName = ot?.synonym || ot?.name || objectName;
          if (!cancelled) setFormTitle(translate('form.title', { name: displayName, action: objectId ? translate('common.edit') : translate('common.creating') }));
        } else if (type === 'task' || type === 'tasks') {
          // ЗАДАЧА (встроенный тип): реквизиты — ЕДИНЫЙ механизм
          // metadata_fields (parent_type='task'); имя типа — metadata_task_types
          const fieldsResp: any = (await readThrough(`fields:task:${objectName}`, () =>
            sdk.metadata.listFields({ entityType: 'task', entityCode: objectName }),
          )).data;
          objFields = (fieldsResp?.items || []) as FieldDef[];
          const ttResp: any = (await readThrough('task-types', () => sdk.task.listTaskTypes({}))).data;
          const tt = (ttResp.items || []).find((x: any) => x.code === objectName);
          const displayName = tt?.synonym || tt?.name || objectName;
          if (!cancelled) setFormTitle(translate('form.title', { name: displayName, action: objectId ? translate('common.edit') : translate('common.creating') }));
        } else if (type === 'entity' || type === 'entities') {
          // Entity fields
          objFields = [
            { name: 'login', dataType: 'text', isRequired: true },
            { name: 'name', dataType: 'text' },
            { name: 'email', dataType: 'text' },
            { name: 'password', dataType: 'text' },
          ];
        }

        if (cancelled) return;

        // 2. Проверяем привязку кастомной формы через единый сервис
        let foundBinding: any = null;
        if (type === 'catalog' || type === 'document' || type === 'order' || type === 'task') {
          const binding = await formBindingService.findBinding(type, objectName, 'object');
          foundBinding = binding;
          if (binding && !cancelled) {
            console.log('[AutoObjectForm] Custom form found:', binding.formName);
            setCustomForm({
              id: binding.formId,
              name: binding.formName,
              layout: binding.layout,
              formModule: binding.formModule,
            });
            // Реквизиты формы (для dynamic_list нужен source)
            try {
              const props = await getSDK().formProperties.getFormProperties({ formId: binding.formId });
              const attrs = (props.properties || []).map((p: any) => ({
                name: p.name,
                source: p.source as any,
                selectMode: String(p.select_mode || p.selectMode || ''),
              }));
              if (!cancelled) {
                setFormAttrs(attrs);
                const modes: Record<string, string> = {};
                for (const a of attrs) {
                  if (a.selectMode) modes[a.name] = a.selectMode;
                }
                setAttrSelectModes(modes);
              }
            } catch (e) { logSwallowed(e, 'components/autoforms/useObjectFormModel.tsx:551'); }
          }
        }

        // 3. Данные: сначала из кэша, потом с сервера
        let row: Record<string, any> = initialData || {};
        if (!initialData && objectId) {
          // Пробуем из кэша (мгновенно)
          row = dataService.getById(schema, dataTable, objectId) || {};
          // Если нет в кэше — загружаем с сервера
          if (!row.id) {
            const fetched = await dataService.fetchById(schema, dataTable, objectId);
            if (fetched) row = fetched;
          }
        }

        setFields(objFields);
        setTabularSections(objTs);

        // Значения перечислений (data_type='enumeration', refTable/refCatalogCode =
        // код перечисления): реквизиты объекта + колонки ТЧ — ЕДИНЫЙ загрузчик
        // (lib/enumOptions, SSoT/SRP) → селекты/синонимы значений.
        {
          const enumFields: any[] = [
            ...objFields,
            ...objTs.flatMap((ts: any) => (ts.columns || [])),
          ];
          const map = await loadEnumOptions(enumFields);
          if (Object.keys(map).length > 0) setEnumOptions(map);
        }

        // Условное оформление (scope='form'): правила объекта + стили
        try {
          const [cf, st] = await Promise.all([
            getSDK().metadata.listConditionalFormats({ scope: 'form', target: `${type}:${objectName}` } as any),
            getSDK().metadata.listStyles({}),
          ]);
          setCondRules((cf as any)?.items || []);
          const sm: Record<string, any> = {};
          for (const s of (st as any)?.items || []) sm[s.name] = s.attributes || {};
          setCondStyles(sm);
        } catch (e) { logSwallowed(e, 'components/autoforms/useObjectFormModel.tsx:592'); }

        // Автозаполнение для новых документов
        if (!objectId && !initialData) {
          const autoFilled: Record<string, any> = {};
          for (const f of objFields) {
            const code = f.code || f.name || '';
            const dt = (f.dataType || f.data_type || '').toLowerCase();
        // Автозаполняем дату текущей датой (как в 1С: номер и дата документа
        // заполняются автоматически; у документа стандартное поле «Дата» — тип date)
        if ((dt === 'timestamp' || dt === 'date') && (code === 'date' || code === 'created_at')) {
          autoFilled[code] = new Date().toISOString().slice(0, 19);
        }
            // Автозаполняем boolean поля значением false
            if (dt === 'boolean') {
              autoFilled[code] = false;
            }
          }
          row = { ...row, ...autoFilled };
        }

        // Значения по умолчанию реквизитов (metadata_fields.default_value):
        // НОВАЯ запись — подставляем там, где ни initialData, ни автозаполнение
        // значение не задали (как в 1С). Enumeration/ссылка — код/uuid строкой,
        // boolean — true/false; прочие — строкой (сервер приводит к типу колонки
        // при записи — ЕДИНЫЙ путь, без дублей с DDL DEFAULT).
        if (!objectId) {
          const withDefaults = { ...row };
          for (const f of objFields) {
            const code = f.code || f.name || '';
            if (!code) continue;
            const raw = f.defaultValue ?? f.default_value;
            if (raw === undefined || raw === null || String(raw) === '') continue;
            const cur = withDefaults[code];
            if (cur !== undefined && cur !== null && cur !== '') continue;
            const dt = String(f.dataType || f.data_type || '').toLowerCase();
            if (dt === 'boolean') {
              const s = String(raw).toLowerCase();
              withDefaults[code] = s === 'true' || s === '1' || s === 'да';
            } else {
              withDefaults[code] = String(raw);
            }
          }
          row = withDefaults;
        }

        // Статус ЗАДАЧИ: схема типа (SSoT) — селект «Статус» показывает ВСЕ
        // статусы схемы; НОВОЙ задаче подставляем начальный (is_initial) —
        // до инициализации значений (дефолт попадает и в автоформу, и в стор
        // кастомной формы). Тот же статус ставит сервер при создании.
        if (type === 'task') {
          let taskStatus: { fieldName: string; statuses: { code: string; name: string }[]; initialCode: string } | null = null;
          try {
            const tsr: any = await getSDK().status.getStatusSchema({ entityType: 'task', entityCode: objectName } as any);
            if (tsr?.id) {
              const statuses: { code: string; name: string; initial: boolean }[] = ((tsr.statuses || []) as any[]).map((s: any) => ({
                code: String(s.code || ''), name: String(s.name || s.code || ''), initial: !!s.isInitial,
              }));
              taskStatus = {
                fieldName: tsr.fieldName || 'status_code',
                statuses: statuses.map((s) => ({ code: s.code, name: s.name })),
                initialCode: (statuses.find((s) => s.initial) || statuses[0] || { code: '' }).code || '',
              };
            }
          } catch (e: any) {
            logSwallowed(e, 'components/autoforms/useObjectFormModel.tsx:task-status-schema');
          }
          if (!cancelled) setTaskStatusSchema(taskStatus);
          if (!objectId && taskStatus?.initialCode) {
            const code = taskStatus.fieldName;
            if (!row[code]) row = { ...row, [code]: taskStatus.initialCode };
          }
        }
        setData(row);
        // ИНИЦИАЛИЗАЦИЯ стора значениями формы — ОДИН раз за СЕАНС формы
        // (initialized[formKey], снимается в closeForm → endFormSession).
        // Повторный load/ремаунт в рамках СЕАНСА (пикеры/модалки/кэш-события)
        // значения НЕ трогает — несохранённый ввод сохраняется по построению:
        //  - существующая запись: данные сервера — начальное состояние, ввод
        //    (prev) приоритетнее (row заполняет отсутствующее: ФИЗИЧЕСКИЕ колонки
        //    + presentation, не затирая виртуальные реквизиты формы «Адрес»);
        //  - НОВАЯ запись: ЧИСТАЯ область значений (defaults/prefill) — значения
        //    других записей НЕ протекают (даже если .new уцелел от сеанса без
        //    closeForm: сброс здесь гарантирует детерминизм).
        if (!useFormStore.getState().initialized[formKey]) {
          if (objectId) {
            useFormStore.getState().setForm(formKey, (prev) => ({ ...row, ...prev }));
          } else {
            // initialData (напр. «+ Создать» из дин.списка: subject_id = текущая
            // заявка) — предзаполнение приоритетнее остатков прежнего ввода.
            useFormStore.getState().setForm(formKey, { ...row });
          }
          useFormStore.getState().markInitialized(formKey);
        }

        // Загрузка строк табличных частей кастомной формы (для существующей записи).
        // Иначе ТЧ показывается пустой после повторного открытия — «данные не сохранились».
        if (foundBinding && objectId && (type === 'catalog' || type === 'document')) {
          let layout: any = {};
          try {
            layout = typeof foundBinding.layout === 'string' ? JSON.parse(foundBinding.layout) : (foundBinding.layout || {});
          } catch { layout = {}; }
          const tsEls = (layout.elements || []).filter((el: any) => el.type === 'tabularSection');
          for (const el of tsEls) {
            if (!el.name) continue;
            const tsTable = `${objectName}_${el.name.toLowerCase()}`;
            try {
              await dataService.fetchAll(schema, tsTable);
              const rows = dataService
                .list<Record<string, unknown>>(schema, tsTable)
                .filter((r) => r.parent_id === objectId);
              if (!cancelled) {
                setCustomTsData((prev) => ({ ...prev, [el.name]: rows }));
              }
            } catch (e: any) {
              log.error('Custom TS load failed', { tsTable, error: e });
            }
          }
        }
        // Событие onOpen модуля ФОРМЫ: выполняется ОДИН раз за время жизни формы
        // (как в 1С «ПриСозданииНаСервере»/метод при создании формы) — при ПЕРВОМ
        // открытии существующей записи. Повторные load (кэш-события dataService,
        // перемонтирование) НЕ должны перезапускать onOpen: он заполняет реквизиты
        // формы (form.setField) из данных объекта и ЗАТИРАЛ бы то, что пользователь
        // уже изменил (диагноз: onOpen при повторном load перезаписывал выбранный
        // из пикера «Адрес» обратно на presentation из БД).
        if (objectId && foundBinding?.formModule && !openedFormKeys.has(formKey)) {
          openedFormKeys.add(formKey);
          const mod = new FormModule();
          (mod as any).moduleCode = foundBinding.formModule;
          (mod as any).parseModule();
          if (mod.hasHandler('onOpen')) {
            const api = createFormAPI({
              values: customValues,
              setValues: setCustomValues,
              setDisplayNames: setCustomDisplayNames,
              tsData: customTsData,
              setTsData: setCustomTsData,
              objectType,
              objectName,
              recordId: objectId,
              openPicker: openModulePicker,
              setError: (e: string | null) => { if (e) setError(e); },
              onClose: closeForm,
              setTitle: setFormTitle,
              setFieldVisible: (name: string, visible: boolean) => setHiddenFields((prev) => ({ ...prev, [name]: !visible })),
            });
            try {
              await mod.execute('onOpen', { data: row }, api);
            } catch (e: any) {
              log.warn('onOpen handler error', { error: e });
            }
          }
        }
      } catch (e: any) {
        log.error('AutoObjectForm load failed', e);
        if (!cancelled) setError(e?.message || translate('form.error.load'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [objectType, objectName, objectId]);

  // Подписка на изменения кэша — авто-обновление при изменении данных
  useEffect(() => {
    if (!objectId) return;
    return dataService.subscribe(schema, table, () => {
      const updated = dataService.getById(schema, table, objectId);
      if (updated) setData(updated);
    });
  }, [objectId, schema, table]);

  // Сохранение через DataService (кэш + сервер)
  const handleSave = async (opts?: { close?: boolean }) => {
    setSaving(true);
    setError(null);
    setValidationError(null);
    const validationError = validateForm();
    if (validationError) {
      setValidationError(validationError);
      setSaving(false);
      return;
    }
    try {
      // Сбор значений при сохранении — ЕДИНЫЕ функции (SRP, вынесены из монолита):
      // saveData (кастомная/автоформа), formAttributes (виртуальные), extraFields.
      const saveData: Record<string, unknown> = collectSaveData({ fields, data, customValues, customForm: !!customForm });
      const formAttributes: Record<string, string> = collectFormAttributes(fields, customValues);

      let recordId = objectId;
      if (type === 'order' || type === 'orders') {
        // ЗАЯВКА: создание/обновление через OrderService (автономер, начальный
        // статус по схеме, права; SSoT — сервер). Произвольные реквизиты —
        // extraFields (сервер пишет ТОЛЬКО существующие колонки).
        const sdkOrder = getSDK();
        const extraFields = collectExtraFields(saveData);
        if (objectId) {
          await sdkOrder.order.updateOrder({ id: objectId, extraFields, formAttributes } as any);
        } else {
          const created: any = await sdkOrder.order.createOrder({
            name: String(extraFields.name || extraFields.phone || translate('form.default_order')),
            extraFields,
            formAttributes,
          } as any);
          recordId = String(created?.orderId || created?.id || '');
        }
      } else if (type === 'task' || type === 'tasks') {
        // ЗАДАЧА: создание/обновление через TaskService (статус по схеме, права;
        // SSoT — сервер). Реквизиты — extraFields (только существующие колонки).
        const extraFields = collectExtraFields(saveData);
        // Предмет/исполнитель — СТАНДАРТНЫЕ поля задачи; в extraFields они могут
        // не попасть (скрытые), поэтому передаём их явно. Иначе задача, созданная
        // из «+ Создать» динамического списка, НЕ связывается с заявкой
        // (subject_id пустой) и не отображается в списке «Задачи по заявке».
        const sd = saveData as Record<string, unknown>;
        const subjectId = String(sd.subject_id || sd.subjectId || '');
        const subjectTable = String(sd.subject_id_table || sd.subjectTable || '');
        const assigneeId = String(sd.assignee_id || sd.assigneeId || '');
        if (objectId) {
          await getSDK().task.updateTask({ id: objectId, extraFields } as any);
        } else {
          const created: any = await getSDK().task.createTask({
            taskType: objectName,
            // Тема: только явно введённая. Пусто — СЕРВЕР заполнит по заявке
            // («Заявка №N») или вернёт «тема обязательна» (без мусорных UUID).
            name: String(extraFields.name || ''),
            ...(assigneeId ? { assigneeId } : {}),
            ...(subjectId ? { subjectId } : {}),
            ...(subjectId && subjectTable ? { subjectTable } : {}),
            extraFields,
            formAttributes,
          } as any);
          recordId = String(created?.id || created?.taskId || '');
        }
      } else if (objectId) {
        // КАСТОМНАЯ ФОРМА (справочник/документ/регистр): единый SaveForm — клиент
        // шлёт {formId, recordId, values}, СЕРВЕР по манифесту (FormManifest) сам
        // делит физические колонки / реквизиты формы (form_attributes → модулю).
        // Эвристики «куда писать» на клиенте больше нет (коллизия «Адрес» снята
        // манифестом: резолв по column_name). Автоформа — как раньше (dataService).
        if (customForm?.id && ['catalog', 'document', 'register'].includes(type)) {
          const values = collectFormValues(customValues);
          recordId = await dataService.saveForm(customForm.id, objectId, values);
        } else {
          await dataService.update(schema, dataTable, objectId, saveData, formAttributes);
        }
      } else {
        if (customForm?.id && ['catalog', 'document', 'register'].includes(type)) {
          const values = collectFormValues(customValues);
          recordId = await dataService.saveForm(customForm.id, '', values);
        } else {
          const created = await dataService.create(schema, dataTable, saveData, formAttributes);
          recordId = created.id as string;
        }
      }

      // Заявки/задачи сохраняются через OrderService/TaskService МИМО dataService —
      // кэш чтения (IndexedDB) НЕ обновляется. Сервер публикует data.order.*/
      // data.task.*, но кэш dataService инвалидируется только здесь (как для
      // post/unpost). Иначе повторное открытие заявки показывает СТАРУЮ запись из
      // кэша (например, прежний адрес), хотя в списке — уже новая.
      if ((type === 'order' || type === 'orders' || type === 'task' || type === 'tasks') && objectId) {
        dataService.invalidate(schema, dataTable);
        const fresh = await dataService.fetchById(schema, dataTable, objectId);
        if (fresh) {
          setData(fresh);
          setCustomValues((prev) => ({ ...prev, ...fresh }));
        }
      }

      // Новая запись: строки ТЧ создавались с временным parent_id —
      // ремапим их на серверный id записи.
      if (!objectId && recordId && tempParentId && tabularSections.length > 0) {
        for (const ts of tabularSections) {
          const tsTable = `${objectName}_${(ts.code || ts.name || '').toLowerCase()}`;
          try {
            await dataService.fetchAll(schema, tsTable);
            const orphanRows = dataService
              .list<Record<string, unknown>>(schema, tsTable)
              .filter((r) => r.parent_id === tempParentId);
            for (const row of orphanRows) {
              await dataService.update(schema, tsTable, String(row.id), { parent_id: recordId });
            }
          } catch (e: any) {
            log.error('TS parent remap failed', { tsTable, error: e });
          }
        }
      }

      // Для форм выбора: сообщаем созданную запись (id + представление)
      if (!objectId && recordId && onCreated) {
        const createdName = String(saveData['synonym'] ?? saveData['name'] ?? saveData['code'] ?? recordId);
        onCreated(recordId, createdName);
      }

      // Сохраняем табличные части кастомной формы
      if (customForm && recordId) {
        await saveCustomTabularSections(recordId);
      }

      recordIdRef.current = recordId || null;

      await lock.release();
      if (opts?.close ?? true) {
        closeForm();
      }
    } catch (e: any) {
      log.error('AutoObjectForm save failed', e);
      // Ошибка СОХРАНЕНИЯ — баннером ПОВЕРХ формы (поля не пропадают);
      // дубликат кода/номера — подсветка соответствующего поля
      const msg = e?.message || translate('form.error.save');
      setSaveError(msg);
      if (typeof msg === 'string' && msg.includes('дубликат')) {
        const dupField = msg.includes('номера') ? 'number' : 'code';
        setFieldErrors((prev) => ({ ...prev, [dupField]: msg }));
      }
    } finally {
      setSaving(false);
    }
  };

  // Проведение документа (модуль onPosting): сохранение → postDocument → обновить
  // состояние → (и закрыть). Модули onPosting вызываются СЕРВЕРОМ (PostDocumentViaModule).
  // Проведённость: listTableData отдаёт boolean СТРОКОЙ ("0"/"1") — нормализуем
  const isPosted = data.is_posted === true || data.is_posted === 1 ||
    String(data.is_posted).toLowerCase() === 'true' || String(data.is_posted) === '1';

  const handleSaveAndPost = async (andClose: boolean) => {
    await handleSave({ close: false });
    const rid = recordIdRef.current;
    if (!rid) return;
    try {
      await getSDK().metadata.postDocument({ documentType: table, documentId: rid });
    } catch (e: any) {
      setSaveError(e?.message || translate('form.error.post'));
      return;
    }
    try {
      // Кэш чтения — иначе fetchById вернёт СТАРОЕ состояние (is_posted)
      dataService.invalidate(schema, table);
      const fetched = await dataService.fetchById(schema, table, rid);
      if (fetched) {
        setData(fetched);
        setCustomValues({ ...fetched });
      }
    } catch (e: any) {
      log.error('reload after post failed', e);
    }
    if (andClose) closeForm();
  };

  // Отмена проведения (модуль onUnposting) — для сохранённого проведённого документа
  const handleUnpost = async () => {
    if (!objectId) return;
    setSaving(true);
    setError(null);
    try {
      await getSDK().metadata.unpostDocument({ documentType: table, documentId: objectId });
      // Кэш чтения — иначе fetchById вернёт СТАРОЕ состояние (is_posted)
      dataService.invalidate(schema, table);
      const fetched = await dataService.fetchById(schema, table, objectId);
      if (fetched) {
        setData(fetched);
        setCustomValues({ ...fetched });
      }
    } catch (e: any) {
      log.error('unpost failed', { documentType: table, id: objectId, error: e?.message || String(e) });
      setError(e?.message || translate('form.error.unpost'));
    } finally {
      setSaving(false);
    }
  };

  // Сохранение табличных частей кастомной формы: создание/обновление/удаление строк
  const saveCustomTabularSections = async (parentId: string) => {
    for (const tsName of Object.keys(customTsData)) {
      const rows = customTsData[tsName] || [];
      if (!rows.length) continue;
      const tsTable = `${objectName.toLowerCase()}_${tsName.toLowerCase()}`;
      try {
        await dataService.fetchAll(schema, tsTable);
        const existing = dataService.list<Record<string, unknown>>(schema, tsTable).filter((r) => r.parent_id === parentId);
        const existingIds = new Set(existing.map((r) => r.id as string).filter(Boolean));
        const currentIds = new Set(rows.map((r) => r.id).filter(Boolean) as string[]);

        // Удаляем убранные строки
        for (const ex of existing) {
          if (!currentIds.has(ex.id as string)) {
            await dataService.remove(schema, tsTable, ex.id as string);
          }
        }

        // Обновляем/создаём строки
        for (const row of rows) {
          const rowData: Record<string, unknown> = { parent_id: parentId };
          for (const [k, v] of Object.entries(row)) {
            if (['id', 'parent_id', 'created_at', 'updated_at', 'created_by', 'updated_by'].includes(k)) continue;
            // Серверные представления ({col}_presentation) НЕ записываются обратно —
            // иначе UPDATE падает: no column named {col}_presentation
            if (k.endsWith('_presentation')) continue;
            if (v !== undefined) rowData[k] = v;
          }
          if (row.id && existingIds.has(row.id as string)) {
            await dataService.update(schema, tsTable, row.id as string, rowData);
          } else {
            await dataService.create(schema, tsTable, rowData);
          }
        }
      } catch (e: any) {
        log.error('Failed to save tabular section', { tsName, tsTable, error: e });
        setError((prev) => prev || translate('form.error.tabular_save', { name: tsName }));
      }
    }
  };

  // Рендер реквизита через единый по-видовой компонент (SRP: классификация
  // полей/ветки anyref/assignee/address/reference/source — в AutoFormField)
  // СУЩЕСТВУЮЩАЯ задача — ТОЛЬКО ЧТЕНИЕ: реквизиты созданной задачи изменяются
  // только командами (статусы/взятие); правила/статусная схема не затронуты.
  // ГОЛОСОВОЙ ВВОД: у ЗАЯВОК (все базы, включая будущие) микрофон доступен во
  // ВСЕХ текстовых полях основной вкладки — платформенное правило, без пометки
  // реквизита; у остальных типов — по пометке voice_input (SSoT — метаданные).
  const withVoice = (f: FieldDef): FieldDef => {
    const dt = String((f as any).dataType ?? (f as any).data_type ?? '');
    if (type === 'order' && dt === 'text') return { ...f, voiceInput: true } as FieldDef;
    return f;
  };
  const renderField = (f: FieldDef) => (
    <AutoFormField
      field={withVoice(f)}
      objectType={type}
      objectName={objectName}
      readOnly={type === 'task' && !!objectId}
      data={data}
      onDataChange={setData}
      fieldErrors={fieldErrors}
      attrSelectModes={attrSelectModes}
      enumOptions={enumOptions}
      options={isTaskStatusField(f.code || f.name) ? taskStatusOptions : undefined}
      extReady={extReady}
      condRules={condRules}
      condStyles={condStyles}
      condCtx={condCtx}
      statusFieldName={statusSchema?.fieldName}
      onSaveError={(msg) => setSaveError(msg)}
      openAnyRefPicker={openAnyRefPicker}
      clearFieldError={clearFieldError}
    />
  );

  // Парсим action bar из layout кастомной формы
  const actionBar = (() => {
    if (!customForm) return [];
    try {
      const layout = typeof customForm.layout === 'string' ? JSON.parse(customForm.layout) : (customForm.layout || {});
      return layout.actionBar || [];
    } catch { return []; }
  })();

  const isDocument = type === 'document' || type === 'documents';
  // Системный справочник «Файлы»: загрузка/скачивание содержимого файла
  const isFileCatalog = type === 'catalog' && (objectName === 'files' || objectName === 'files_po_dokumentam');
  const [fileBusy, setFileBusy] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setFileBusy(true);
    setFileError(null);
    try {
      const buf = await file.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      const data = btoa(binary);
      const info = await getSDK().files.uploadFile({
        data: new Uint8Array(buf) as any,
        fileName: file.name,
        fileType: file.type || 'application/octet-stream',
        ownerId: String((data as any).owner_id ?? ''),
      });
      setData((prev) => ({
        ...prev,
        name: info.fileName,
        file_name: info.fileName,
        file_size: Number(info.fileSize),
        file_type: info.fileType,
        storage_code: info.storageCode,
        file_key: info.fileKey,
        file_hash: info.fileHash,
      }));
    } catch (err: any) {
      setFileError(err?.message || translate('form.error.file_load'));
    } finally {
      setFileBusy(false);
    }
  };

  const handleFileDownload = async () => {
    if (!objectId) return;
    setFileBusy(true);
    setFileError(null);
    try {
      // Оффлайн-first: содержимое из кэша (IndexedDB) или с сервера (on-demand)
      const { blob, fileName } = await ensureFileBlob(objectId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'file';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setFileError(err?.message || translate('form.error.file_download'));
    } finally {
      setFileBusy(false);
    }
  };

  // Валидация обязательных полей («Проверка заполнения» в метаданных):
  // блокирует сохранение и подсвечивает незаполненные поля.
  // КАСТОМНАЯ форма: проверяются ТОЛЬКО поля, которые РЕАЛЬНО размещены на
  // макете (видны пользователю). Обязательное поле, которого НЕТ на форме
  // (например «Тема» у заявки, форма собирает Заказчик/Адрес/…) — НЕ проверяется:
  // форма его не запрашивает. Автоформа: проверяются все обязательные поля.
  const validateForm = (): string | null => {
    // ЧИСТАЯ валидация (SRP, вынесена в formValidate.ts): расчёт незаполненных
    // обязательных полей; применение ошибок к state — здесь.
    const result = validateFormFields({
      fields,
      values: customForm ? customValues : data,
      customForm: !!customForm,
      layout: customForm?.layout,
      isNew: !objectId,
    });
    setFieldErrors(result.errors);
    return result.errorMessage;
  };

  const clearFieldError = (code: string) => {
    setFieldErrors((prev) => {
      if (!prev[code]) return prev;
      const next = { ...prev };
      delete next[code];
      return next;
    });
    setValidationError(null);
  };

  const customFormLayout = (() => {
    if (!customForm) return {};
    try {
      return typeof customForm.layout === 'string' ? JSON.parse(customForm.layout) : (customForm.layout || {});
    } catch { return {}; }
  })();
  const formReadOnly = customForm ? (customForm.viewOnly || (customFormLayout as any).readOnly || (customFormLayout as any).viewOnly || false) : false;
  const formEnabled = customForm ? ((customFormLayout as any).enabled !== false) : true;
  // Атрибуты для макета КАСТОМНОЙ формы (SSoT — метаданные + реквизиты формы):
  // реквизиты ОБЪЕКТА + виртуальные реквизиты ФОРМЫ (см. E2e7/comment ниже).
  const customFormObjectAttrs = useMemo(() => [
    ...fields.map((f) => {
      const refTable = f.refTable || f.refCatalogCode || (f as any).ref_table || '';
      const dt = String(f.dataType || f.data_type || '').toLowerCase();
      return {
        name: f.code || f.name || '',
        type: f.dataType || f.data_type || 'string',
        label: f.synonym || '',
        selectionParams: (f as any).selectionParams,
        selectionLinks: (f as any).selectionLinks,
        fkTable: refTable,
        types: Array.isArray((f as any).types) && (f as any).types.length > 0 ? (f as any).types : [],
        // Перечисление на макете — селект с синонимами значений (SSoT — сервер).
        // Статус ЗАДАЧИ — из СТАТУСНОЙ СХЕМЫ (все статусы схемы), не перечисление.
        options: isTaskStatusField(f.code || f.name)
          ? taskStatusOptions
          : (dt === 'enumeration' && refTable ? enumOptions[refTable] : undefined),
        // Голосовой ввод: пометка реквизита доходит до кастомных форм
        // (LayoutField.voiceInputFor) — раньше флаг терялся в этой карте.
        voiceInput: (f as any).voiceInput === true || (f as any).voice_input === true,
        // Объект-родитель у поля (как в дереве конфигуратора): нужен правилам
        // default_for дополнений (object_type/object_code), напр. «Применение
        // шаблона отклика» на поле mkt_responses.comment.
        parentType: type,
        parentCode: objectName,
      };
    }),
    // РЕКВИЗИТЫ ФОРМЫ (виртуальные, text) — рендерятся тем же FormFieldRenderer
    // (дополнения платформы применяются и к ним: «Дополнение адреса» для текстовых
    // полей). ВАЖНО (коллизия «Адрес»/address_id): исключаем реквизит формы, только
    // если он совпадает с code (column_name) реквизита объекта, НЕ с name (синонимом)!
    // У address_id code='address_id', name='Адрес' — реквизит формы «Адрес»
    // (виртуальный text) НЕ совпадает ни с одним column_name → остаётся в attributes
    // и рендерится как AddressField. Иначе (сравнение по name) он отфильтровывался →
    // поле вело себя недетерминированно (у существующей записи не обновлялось при
    // выборе из пикера).
    ...formAttrs
      .filter((a) => a.name && !fields.some((f) => (f.code || '') === a.name))
      .map((a) => ({ name: a.name, type: 'text', label: a.name })),
  ], [fields, formAttrs, enumOptions, taskStatusOptions, isTaskStatusField, type, objectName]);
  const customFormTabularSections = useMemo(() => tabularSections.map((ts) => ({
    name: ts.code || ts.name || '',
    synonym: ts.synonym || ts.name || ts.code || '',
    attributes: (ts.columns || []).map((c) => {
      const refTable = c.refTable || (c as any).refCatalogCode || '';
      const dt = String(c.dataType || '').toLowerCase();
      return {
        name: c.name,
        synonym: (c as any).synonym || c.name,
        type: c.dataType || 'string',
        refTable,
        selectionParams: (c as any).selectionParams,
        selectionLinks: (c as any).selectionLinks,
        // Перечисление в колонке ТЧ — селект с синонимами значений
        options: dt === 'enumeration' && refTable ? enumOptions[refTable] : undefined,
      };
    }),
  })), [tabularSections, enumOptions]);
  // Права объекта (как в 1С): нет «Изменения» у существующей / «Добавления» у новой —
  // форма только для чтения (сервер всё равно защищает — это UX-скрытие)
  const noObjectRight = objectRights
    ? (objectId ? !objectRights.canUpdate : !objectRights.canCreate)
    : false;

  // Стандартные команды объекта, скрываемые на КЛИЕНТЕ (UX-скрытие; серверные
  // права всё равно защищают запись):
  //  - take — задача уже назначена исполнителю (в этой конфигурации пула нет);
  //  - delete — пользователь не админ и не создатель записи.
  // Создатель — author_id (задачи) или created_by (аудит-поле записи).
  // Нормализация аудит-полей: значение может прийти строкой, а может — объектом
  // (proto-обёртка {value}/{String}) или числом; сравниваем ТОЛЬКО с явным id.
  const normId = (v: any): string => {
    if (v === null || v === undefined) return '';
    if (typeof v === 'string') return v.trim();
    if (typeof v === 'object') return String((v as any).value ?? (v as any).String ?? '').trim();
    return String(v).trim();
  };
  const meId = normId(currentUser?.id);
  const createdBy = normId(data?.created_by);
  const authorId = normId(data?.author_id);
  const isRecordCreator = !!meId && (createdBy === meId || authorId === meId);
  // Создатель ИЗВЕСТЕН, если аудит-поле непустое. Если оно не загрузилось —
  // НЕ прячем кнопку: право жёстко проверит СЕРВЕР (создатель/админ), иначе
  // создатель оставался без кнопки (жалоба «в заявке нету кнопки удалить»).
  const creatorKnown = createdBy !== '' || authorId !== '';
  const hiddenObjectCommands: string[] = [];
  if (String(data?.assignee_id || '').trim() !== '') hiddenObjectCommands.push('take');
  // «Удалить» в задачах/заявках НЕ скрываем на клиенте вообще: надёжно определить
  // создателя здесь нельзя (формат аудит-поля/кэш), из-за чего у Создателя
  // кнопка пропадала. Право жёстко проверяет СЕРВЕР (создатель/админ) — при
  // попытке удалить чужое вернётся внятная ошибка.
  void creatorKnown;
  void isRecordCreator;
  // Окончательное (физическое) удаление задач/заявок — только администратор.
  if (!isAdmin) hiddenObjectCommands.push('delete_permanent');
  // ФИНАЛЬНЫЙ СТАТУС задачи (Выполнена/Отменена, 2026-10-15): команды
  // «Выполнено»/«Отменить» СКРЫТЫ (переходы из финального статуса схемой
  // запрещены), «Удалить» — только администратору (защита истории работ).
  {
    const statusCode = String((data as any)?.status_code || '');
    const finalStatus = type === 'task' && (statusCode === 'done' || statusCode === 'canceled');
    if (finalStatus) {
      hiddenObjectCommands.push('done', 'canceled');
      if (!isAdmin) hiddenObjectCommands.push('delete');
    }
  }
  // «Восстановить» — только для ПОМЕЧЕННЫХ записей (снятие пометки).
  if (!(data as any)?.is_marked) hiddenObjectCommands.push('restore');

  // Параметры выбора и связи параметров выбора (как в 1С) для пикера —
  // чистые хелперы (SRP, objectFormHelpers.ts): buildSelectionFilters/buildCreateData.

  const handlePick = (attr: { name: string; types?: Array<{ typeName: string; fullName: string; objectType: string }> | string[]; fkTable?: string; refTable?: string; selectionParams?: Array<{ field: string; condition: string; value?: string }>; selectionLinks?: Array<{ param: string; field: string }> }, tsName?: string, rowIndex?: number) => {
    // Нормализация типов: дескрипторы-строки ('Справочник.X'/'entity.entities') →
    // структура {typeName, fullName, objectType} — в чистом хелпере (SSoT)
    const context = tsName !== undefined && rowIndex !== undefined
      ? (customTsData[tsName] || [])[rowIndex] || {}
      : customValues;
    setPickingAttr({
      name: attr.name,
      types: normalizePickTypes(attr),
      tsName,
      rowIndex,
      selectMode: attrSelectModes[attr.name] || '',
      filters: buildSelectionFilters(attr, context),
      createInitialData: buildCreateData(attr, context),
    });
    setPickerOpen(true);
  };

  const handlePickTs = (tsName: string, rowIndex: number, colAttr: { name: string; types?: Array<{ typeName: string; fullName: string; objectType: string }> }) => {
    handlePick(colAttr, tsName, rowIndex);
  };

  // «Создать» в дин.списке: форма объекта ИСТОЧНИКА + prefill из отборов.
  // srcTable — таблица ИСТОЧНИКА (сам список), table — таблица ФОРМЫ (текущий объект).
  const handleDLCreate = (srcTable: string, filters: Array<{ field: string; condition: string; value?: string }>, objectId: string) => {
    const initial: Record<string, unknown> = {};
    for (const f of filters || []) {
      if (f.condition === '=' && f.field) {
        initial[f.field] = f.value === '@objectId' ? objectId : (f.value || '');
      }
    }
    // anyref-полям нужна и таблица типа ({col}_table). Для ссылки на ТЕКУЩИЙ
    // объект (@objectId) это таблица ФОРМЫ (напр. заявка → 'orders'), а НЕ
    // таблица списка (иначе представление «Предмет» не резолвится: искали бы
    // id заявки в таблице задач). Для статических значений тип неизвестен —
    // серверный ResolveReference найдёт его перебором таблиц состава.
    for (const f of filters || []) {
      const key = f.field;
      if (f.condition === '=' && key && f.value === '@objectId' && initial[key]) {
        initial[key + '_table'] = table;
      }
    }
    // Задача по ЗАЯВКЕ («+ Создать» в «Задачи по заявке»): предзаполняем ТЕМУ
    // номером заявки — обязательное поле не должно пустовать в форме создания.
    // Описание заполняет СЕРВЕР из заявки (единая точка — независимо от способа
    // создания: динамический список, «Назначить исполнителя», модуль).
    if (type === 'order' && initial.subject_id && !initial.name) {
      const num = String((data as Record<string, unknown>)?.number || '');
      initial.name = num ? translate('form.default_order_number', { number: num }) : translate('form.default_order');
    }
    void (async () => {
      try {
        const srcType = await resolveDlSourceType(srcTable);
        if (!srcType) {
          useToastStore.getState().showError(`Источник дин.списка не распознан: таблица «${srcTable}» не найдена среди задач, справочников, документов и регистров.`);
          return;
        }
        setDlCreate({ type: srcType, name: srcTable, initial });
      } catch (e) {
        useToastStore.getState().showError(friendlyError(e));
      }
    })();
  };

  // ВЫБОР строки дин.списка: открыть запись источника (как в 1С — клик по
  // строке подчинённого списка открывает форму объекта). Тип — резолвом
  // таблицы (task → catalog → document → register), SSoT.
  const handleDLOpen = (srcTable: string, id: string) => {
    if (!id) return;
    void (async () => {
      try {
        const srcType = await resolveDlSourceType(srcTable);
        if (!srcType) {
          useToastStore.getState().showError(`Источник дин.списка не распознан: таблица «${srcTable}» не найдена среди задач, справочников, документов и регистров.`);
          return;
        }
        setDlOpen({ type: srcType, name: srcTable, id });
      } catch (e) {
        useToastStore.getState().showError(friendlyError(e));
      }
    })();
  };

  const handlePicked = (id: string, name: string) => {
    if (!pickingAttr) return;
    log.info('picker: selected', { attr: pickingAttr.name, ts: pickingAttr.tsName, row: pickingAttr.rowIndex, id, name });
    // Имя поля может быть ПУТЁМ («Объект.manager») — значение пишется по последнему
    // сегменту (рендер читает valueKey = path.split('.').pop())
    const key = pickingAttr.name.split('.').pop() || pickingAttr.name;
    const { tsName, rowIndex } = pickingAttr;
    if (tsName !== undefined && rowIndex !== undefined) {
      setCustomTsData((prev: any) => ({
        ...prev,
        [tsName]: (prev[tsName] || []).map((r: any, i: number) => (i === rowIndex ? { ...r, [key]: id, [key + '_presentation']: name } : r)),
      }));
    } else {
      setCustomValues((prev) => ({ ...prev, [key]: id }));
      setCustomDisplayNames((prev) => ({ ...prev, [key]: name }));
    }
    setPickerOpen(false);
    setPickingAttr(null);
  };

  const openModulePicker = (type: string, callback: (result: { id: string; name: string; objectType: string; objectName: string }) => void) => {
    const parsed = parseModulePickerType(type);
    if (!parsed) {
      setError(`Не удалось открыть подбор: неизвестный тип «${type}». Используйте формат: catalog.Counterparties или document.Sales`);
      return;
    }
    setModulePicker({ open: true, ...parsed, callback });
  };

  // Выполнение встроенных/пользовательских команд формы
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignBusy, setAssignBusy] = useState(false);
  // «Передать задачу» (стандартная команда transfer, RPC TaskService.TransferTask):
  // диалог выбора исполнителя → серверная замена assignee (право task:edit/админ).
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferBusy, setTransferBusy] = useState(false);

  const doTransfer = async (entityId: string) => {
    if (!entityId || !objectId) return;
    setTransferBusy(true);
    setError(null);
    try {
      await getSDK().task.transferTask({ id: objectId, assigneeId: entityId } as any);
      const fetched = await dataService.fetchById(schema, table, objectId);
      if (fetched) setData(fetched);
      useToastStore.getState().showSuccess(translate('form.transfer.done'));
      emitDataChanged({ table, action: 'updated' });
      setTransferOpen(false);
    } catch (e: any) {
      log.error('transfer: failed', e);
      setError(friendlyError(e));
    } finally {
      setTransferBusy(false);
    }
  };

  const doAssignExecutor = async (entityId: string) => {
    if (!entityId || !objectId) return;
    log.info('assign_executor: start', { entityId, objectId, table, type });
    setAssignBusy(true);
    setError(null);
    try {
      // Кнопка «Назначить» на заявке (встроенная команда assign_executor):
      // создаёт задачу типа «Задачи по заявкам» с исполнителем и предметом=заявка.
      // Тип задач — ЕДИНСТВЕННЫЙ с полем subject_id (anyref) и предметом=текущий
      // объект (детерминированно; пользовательская поставка может задать свой).
      const tts: any = await getSDK().task.listTaskTypes({});
      const taskType = (tts?.items || []).find((t: any) => t.code === 'tasks_by_orders') || (tts?.items || [])[0];
      if (!taskType) {
        setError(translate('form.transfer.no_task_type'));
        return;
      }
      await getSDK().task.createTask({
        taskType: taskType.code,
        // Тема/Описание — заполняет СЕРВЕР из заявки (единая точка: «Заявка №N» +
        // Заказчик/Адрес/Телефон/Желаемая дата и время) — независимо от способа.
        assigneeId: entityId,
        subjectTable: table,
        subjectId: objectId,
      } as any);
      useToastStore.getState().showSuccess(translate('form.transfer.created'));
      log.info('assign_executor: created task ok', { objectId, entityId });
      // Задача создана сервером — локальное событие: списки заявки И рабочие столы
      // (виджет «Мои задачи») обновляются СРАЗУ, без ручного обновления.
      emitDataChanged({ table, action: 'created' });
      setAssignOpen(false);
    } catch (e: any) {
      log.error('assign_executor: failed', e);
      setError(friendlyError(e));
    } finally {
      setAssignBusy(false);
    }
  };

  const runCommand = async (cmd: string) => {
    if (!cmd) return;
    const builtIn = ['save', 'saveAndClose', 'post', 'postAndClose', 'close', 'refresh'];
    // Стандартные команды ЗАДАЧ (статусная модель): завершить/отменить/взять/в пул
    const taskCommands = ['done', 'canceled', 'take', 'release'];
    if (taskCommands.includes(cmd) && objectType === 'task' && objectId) {
      try {
        if (cmd === 'done' || cmd === 'canceled') {
          // Комментарий исполнителя (как в 1С «Выполнено/Отменено с комментарием»)
          const action = cmd === 'done' ? translate('common.done') : translate('common.canceled');
          const comment = await promptDialog(translate('form.comment_title', { action }), '', action, translate('common.optional'));
          if (comment === null) return; // отмена диалога — статус не меняем
          await getSDK().task.setTaskStatus({ id: objectId, toCode: cmd, comment } as any);
        } else if (cmd === 'take') {
          await getSDK().task.takeTask({ id: objectId } as any);
        } else if (cmd === 'release') {
          await getSDK().task.releaseTask({ id: objectId } as any);
        }
        const fetched = await dataService.fetchById(schema, table, objectId);
        if (fetched) setData(fetched);
        // Смена статуса (в т.ч. done/canceled) — событие обновления списков
        emitDataChanged({ table, action: 'updated' });
        // «Выполнено»/«Отменено» — форма ЗАКРЫВАЕТСЯ (как «Провести и закрыть»),
        // чтобы не оставалась висеть после завершения задачи.
        if (cmd === 'done' || cmd === 'canceled') closeForm();
      } catch (e: any) {
        setError(e?.message || translate('form.error.status'));
      }
      return;
    }
    // Стандартная команда «Удалить»/«Удалить окончательно» (полоса команд
    // задачи/заявки): подтверждение, удаление через СВОЙ сервис (task/order —
    // права создатель/админ). ОБЫЧНОЕ удаление — ПОМЕТКА на удаление (запись
    // скрывается из списков, как в 1С); ОКОНЧАТЕЛЬНОЕ (физическое) — только
    // администратор (кнопка скрыта для не-админов; сервер защищает). После
    // успеха — событие «данные изменились» + закрытие формы.
    if (cmd === 'delete' || cmd === 'delete_permanent') {
      if (!objectId) return;
      const permanent = cmd === 'delete_permanent';
      const question = permanent
        ? translate('form.delete_permanent_confirm')
        : (type === 'order'
          ? translate('form.mark_order_delete_confirm')
          : translate('form.mark_record_delete_confirm'));
      const confirmed = await confirmDialog(question);
      if (!confirmed) return;
      setSaving(true);
      setError(null);
      try {
        if (type === 'task') {
          await getSDK().task.deleteTask({ id: objectId, permanent } as any);
        } else if (type === 'order') {
          await getSDK().order.deleteOrder({ id: objectId, permanent } as any);
        } else {
          await getSDK().metadata.deleteTableRow({ schema, table, rowId: objectId, permanent } as any);
        }
        emitDataChanged({ table, action: 'deleted' });
        closeForm();
      } catch (e: any) {
        setError(e?.message || translate('form.error.delete'));
      } finally {
        setSaving(false);
      }
      return;
    }

    // «Восстановить» (снятие пометки удаления) — для задач/заявок; запись
    // остаётся открытой, флаг is_marked обновляется локально + событие спискам.
    if (cmd === 'restore') {
      if (!objectId) return;
      setSaving(true);
      setError(null);
      try {
        if (type === 'task') {
          await getSDK().task.restoreTask({ id: objectId } as any);
        } else if (type === 'order') {
          await getSDK().order.restoreOrder({ id: objectId } as any);
        } else {
          await getSDK().metadata.updateTableRow({ schema, table, rowId: objectId, values: { is_marked: false } } as any);
        }
        setData((d: any) => ({ ...(d || {}), is_marked: false }));
        emitDataChanged({ table, action: 'updated' });
      } catch (e: any) {
        setError(e?.message || translate('form.error.restore'));
      } finally {
        setSaving(false);
      }
      return;
    }
    // «Передать» (стандартная команда transfer): диалог выбора исполнителя
    // (AssigneePicker — загруженность), замена исполнителя на сервере.
    if (cmd === 'transfer' && objectType === 'task' && objectId) {
      setTransferOpen(true);
      return;
    }
    // «Назначить исполнителя» на заявке (кнопка actionBar, команда
    // assign_executor): окно выбора исполнителя (загруженность) → создание
    // задачи по заявке с исполнителем и предметом=заявка.
    if (cmd === 'assign_executor') {
      if (!objectId) {
        setError(translate('form.task_subject_required'));
        return;
      }
      setAssignOpen(true);
      return;
    }
    if (builtIn.includes(cmd)) {
      if (cmd === 'close') { closeForm(); return; }
      if (cmd === 'refresh') {
        if (objectId) {
          const fetched = await dataService.fetchById(schema, table, objectId);
          if (fetched) setData(fetched);
        }
        return;
      }
      // save / post / *AndClose — используем единое сохранение
      await handleSave();
      return;
    }

    if (customForm?.formModule) {
      const mod = new FormModule();
      (mod as any).moduleCode = customForm.formModule;
      (mod as any).parseModule();
      if (mod.hasHandler(cmd)) {
        const api = createFormAPI({
          values: customValues,
          setValues: setCustomValues,
          setDisplayNames: setCustomDisplayNames,
          tsData: customTsData,
          setTsData: setCustomTsData,
          objectType,
          objectName,
          recordId: objectId,
          openPicker: openModulePicker,
          setError: (e) => { if (e) setError(e); },
          onClose: closeForm,
          setTitle: setFormTitle,
          setFieldVisible: (name, visible) => setHiddenFields((prev) => ({ ...prev, [name]: !visible })),
        });
        try {
          await mod.execute(cmd, { data: customValues }, api);
        } catch (e: any) {
          log.warn('Command error', { cmd, error: e });
          setError(e?.message || `Ошибка выполнения команды ${cmd}`);
        }
      } else {
        console.warn(`[AutoObjectForm] Handler ${cmd} not found in form module`);
      }
    } else {
      console.log('[AutoObjectForm] Custom form command:', cmd);
    }
  };

  // Диалоговые обработчики выбора (перенесены из JSX-инлайнов ObjectFormBody
  // в модель — именованные функции; сигнатуры ObjectFormDialogs соблюдены).
  const handleAnyRefValueSelect = (id: string, name: string) => {
    const picking = anyRefPicker?.pickingType;
    const field = anyRefPicker?.field || '';
    if (!picking) return;
    setData((prev) => ({
      ...prev,
      [field]: id,
      [field + '_table']: picking.objectName.toLowerCase(),
      [field + '_presentation']: name,
    }));
    clearFieldError(field);
    setAnyRefPicker(null);
  };

  const handleAnyRefFile = (file: File) => {
    const field = anyRefPicker?.field || '';
    if (!field || !anyRefPicker) return;
    void (async () => {
      const buf = await file.arrayBuffer();
      const bytes = new Uint8Array(buf);
      try {
        const info: any = await getSDK().files.uploadFile({ data: bytes as any, fileName: file.name, fileType: file.type || '' });
        const id = String(info?.id || '');
        if (!id) return;
        setData((prev) => ({
          ...prev,
          [field]: id,
          [field + '_table']: 'files',
          [field + '_presentation']: file.name,
        }));
        clearFieldError(field);
        setAnyRefPicker(null);
      } catch (err: any) {
        useToastStore.getState().showError(err?.message || translate('form.error.file_load'));
      }
    })();
  };

  const handleMultiSelect = (id: string, name: string) => {
    if (!multiPick) return;
    const tsName = multiPick.tsName;
    const colName = multiPick.colMeta.name || '';
    setCustomTsData((prev: any) => ({
      ...prev,
      [tsName]: [...(prev[tsName] || []), { [colName]: id, [colName + '_presentation']: name }],
    }));
    setMultiPick(null);
  };

  const handleModuleSelect = (id: string, name: string) => {
    if (!modulePicker) return;
    modulePicker.callback({ id, name, objectType: modulePicker.objectType, objectName: modulePicker.objectName });
    setModulePicker(null);
  };

  // Вкладки формы (второй ряд экшен-панели): Основное + подчинённые + Задачи/Файлы
  const tabs: Array<{ id: string; label: string }> = [{ id: 'main', label: translate('form.tab.main') }];
  for (const s of subordinates) tabs.push({ id: `sub:${s.name}`, label: s.synonym });
  if (objectId && tasksLinked) tabs.push({ id: 'tasks', label: translate('form.tab.tasks') });
  if (filesLinked) tabs.push({ id: 'files', label: translate('form.tab.files') });

  // ЕДИНЫЙ объект возврата модели: ВСЕ значения, которые использует JSX
  // AutoObjectForm (кардвью-ветка + main-return) и диалоговые оверлеи.
  // Имена полей совпадают с локальными именами исходного монолита — JSX
  // не меняется (только пролог: вызов хука). setX возвращаются как есть
  // (React гарантирует стабильность ссылок).
  return {
    // Состояния формы
    fields, tabularSections, data, dlCreate, dlOpen, loading, error, saveError, saving, moreOpen,
    customForm, customValues, customDisplayNames, customTsData, formAttrs, fieldErrors, validationError,
    cardView, registerTable, auditEnabled, historyOpen, catalogHierarchyType, catalogOwners, subordinates,
    activeTab, tasksLinked, filesLinked, attrSelectModes, enumOptions, condRules, condStyles, condCtx,
    formTitle, hiddenFields, pickerOpen, pickingAttr, multiPick, modulePicker, anyRefPicker, chatOpen,
    chatUnread, canViewAudit, objectRights, statusSchema, statusPanel, statusBusy, fileBusy, fileError,
    assignOpen, assignBusy, transferOpen, transferBusy,
    // Производные значения
    type, geometryKey, formGeometry, formKey, tsParentId, dataTable, table, schema, lock, audit,
    isPosted, actionBar, isDocument, isFileCatalog, customFormLayout, formReadOnly, formEnabled,
    customFormObjectAttrs, customFormTabularSections, noObjectRight, tabs, isAdmin, currentUser, statusField,
    hiddenObjectCommands,
    // Сеттеры наружу (для JSX-пропсов/диалогов; React-стабильные)
    setData, setCustomValues, setCustomDisplayNames, setCustomTsData, setActiveTab, setMoreOpen,
    setChatOpen, setHistoryOpen, setCardView, setPickerOpen, setPickingAttr, setMultiPick,
    setModulePicker, setAnyRefPicker, setDlCreate, setDlOpen, setAssignOpen, setAssignBusy, setTransferOpen, setTransferBusy, setSaveError,
    clearFieldError,
    // Действия/обработчики формы
    handleSave, handleSaveAndPost, handleUnpost, applyStatus, validateForm, handleFileUpload,
    handleFileDownload, renderField, handlePick, handlePickTs, handleDLCreate, handleDLOpen, handlePicked,
    openModulePicker, doAssignExecutor, doTransfer, runCommand, openAnyRefPicker, closeForm,
    // Диалоговые обработчики выбора (перенесены из JSX-инлайнов в модель)
    handleAnyRefValueSelect, handleAnyRefFile, handleMultiSelect, handleModuleSelect,
  };
}
